/**
 * Runs before React when Defera is running inside the iOS app (Capacitor).
 * On the web every function here is a no-op.
 *
 * Inside the app the web build is bundled on the phone and served from
 * capacitor://localhost, so:
 *   - API requests written same-origin ('/api/…', a few hundred call sites)
 *     go to https://defera.app instead — fetch, XHR, sendBeacon, EventSource;
 *   - links to other sites open in an in-app Safari sheet rather than
 *     replacing the app, and links to defera.app pages stay in the app;
 *   - pages don't pinch-zoom or zoom in on a tapped field, like any app;
 *   - <html> gets .native-app for the few styles that differ;
 *   - wording that reads like a loan is rephrased (appStoreWording.js).
 *
 * The status bar (light text, above the page, never over it) is set in
 * capacitor.config.json rather than here: iOS re-applies that config every
 * time the app's screen reappears, e.g. after an in-app Safari sheet closes.
 */
import { Capacitor } from '@capacitor/core';
import { setOptedOutHere, hasOptedOutHere } from '@/lib/privacyChoices';
import { installAppStoreWording } from '@/native/appStoreWording';

export const isNativeApp = Capacitor.isNativePlatform();
const API_ORIGIN = 'https://defera.app';
const SITE_HOSTS = new Set(['defera.app', 'www.defera.app']);

/** Same-origin API URL → the production API. Everything else is untouched. */
export function toApiUrl(url, localOrigin = typeof window !== 'undefined' ? window.location.origin : '') {
  if (typeof url !== 'string') return url;
  if (url === '/api' || url.startsWith('/api/')) return API_ORIGIN + url;
  if (localOrigin && url.startsWith(`${localOrigin}/api/`)) return API_ORIGIN + url.slice(localOrigin.length);
  return url;
}

function installApiShim() {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (typeof input === 'string') return originalFetch(toApiUrl(input), init);
    if (input instanceof URL) return originalFetch(toApiUrl(input.toString()), init);
    if (typeof Request !== 'undefined' && input instanceof Request) {
      const rewritten = toApiUrl(input.url);
      return originalFetch(rewritten === input.url ? input : new Request(rewritten, input), init);
    }
    return originalFetch(input, init);
  };

  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
    return open.call(this, method, toApiUrl(String(url)), ...rest);
  };

  if (navigator.sendBeacon) {
    const beacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => beacon(toApiUrl(String(url)), data);
  }

  if (window.EventSource) {
    const NativeEventSource = window.EventSource;
    const Patched = function EventSource(url, config) {
      return new NativeEventSource(toApiUrl(String(url)), config);
    };
    Patched.prototype = NativeEventSource.prototype;
    Object.assign(Patched, { CONNECTING: 0, OPEN: 1, CLOSED: 2 });
    window.EventSource = Patched;
  }
}

/** Where a tapped URL should go: 'app' (a page of ours), 'browser', or null (leave it). */
export function classifyLink(href, localOrigin) {
  let url;
  try { url = new URL(href, localOrigin); } catch { return null; }
  if (url.origin === localOrigin) return null; // in-app route, the router handles it
  if (!/^https?:$/.test(url.protocol)) return null; // mailto:, tel:, sms: → iOS handles them
  if (SITE_HOSTS.has(url.hostname)) return { kind: 'app', path: `${url.pathname}${url.search}${url.hash}` };
  return { kind: 'browser', url: url.toString() };
}

async function openInBrowser(url) {
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'popover' });
}

function installLinkHandling() {
  const localOrigin = window.location.origin;
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) return;
    const target = classifyLink(anchor.getAttribute('href'), localOrigin);
    if (!target) return;
    event.preventDefault();
    if (target.kind === 'app') window.location.assign(target.path);
    else openInBrowser(target.url);
  }, true);

  const originalOpen = window.open.bind(window);
  window.open = (href, ...rest) => {
    const target = href ? classifyLink(String(href), localOrigin) : null;
    if (!target) return originalOpen(href, ...rest);
    if (target.kind === 'app') window.location.assign(target.path);
    else openInBrowser(target.url);
    return null;
  };
}

/**
 * No ad tracking in the app. Apple counts sending a purchase to an ad network
 * for measurement as tracking, which needs an App Tracking Transparency prompt.
 * Instead the app records "Do Not Sell or Share" on this device, and the
 * existing account sync (usePrivacyChoiceSync) records it on the customer's
 * account when they sign in, so server/openai-ads-conversions.js leaves their
 * bookings out, exactly as for a visitor who opted out on /privacy-choices.
 */
export function optOutOfAdTrackingInApp() {
  if (!hasOptedOutHere()) setOptedOutHere(true);
}

function lockZoom() {
  const viewport = document.querySelector('meta[name="viewport"]');
  viewport?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
}

/**
 * Live updates (server/ota.js): tell the updater this web build started. A
 * downloaded build that doesn't report in within 10 seconds is rolled back to
 * the last one that did, so this runs first, before any network request. App
 * Store versions from before the updater (1.0) don't have the plugin, and the
 * call fails quietly there.
 */
export async function confirmWebBuildStarted(loadUpdater = () => import('@capgo/capacitor-updater')) {
  try {
    const { CapacitorUpdater } = await loadUpdater();
    await CapacitorUpdater.notifyAppReady();
  } catch {
    // No updater in this App Store version.
  }
}

export function bootstrapNative() {
  if (!isNativeApp) return;
  confirmWebBuildStarted();
  document.documentElement.classList.add('native-app');
  optOutOfAdTrackingInApp();
  lockZoom();
  installApiShim();
  installLinkHandling();
  installAppStoreWording();
}
